import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    CalendarCheck,
    CheckCircle2,
    XCircle,
    Home,
    CreditCard,
    Clock,
    Eye,
    Image as ImageIcon
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageHeader } from './ui/PageHeader';
import { StatusBadge } from './ui/StatusBadge';

const BookingApprovals: React.FC = () => {
    const [bookings, setBookings] = useState<any[]>([]);
    const [filter, setFilter] = useState('pending');

    useEffect(() => { fetchAll(); }, []);

    const fetchAll = async () => {
        const res = await api.get('/bookings/all');
        setBookings(res.data);
    };

    const handleReconcile = async (id: string) => {
        try {
            await api.patch(`/bookings/${id}/reconcile`);
            fetchAll();
        } catch (e) { alert('Action failed'); }
    };

    const handleReject = async (id: string) => {
        try {
            await api.patch(`/bookings/${id}`, { status: 'cancelled' });
            fetchAll();
        } catch (e) { alert('Action failed'); }
    };

    const filtered = bookings.filter(b => b.status === filter);

    return (
        <div className="space-y-6">
            <PageHeader title="Facility Bookings" subtitle="Review and confirm resident reservations">
                <div className="flex bg-slate-100 p-1 rounded-lg">
                    {['pending', 'confirmed', 'cancelled'].map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            className={cn(
                                "px-4 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition-all",
                                filter === s ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                            )}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </PageHeader>

            <div className="space-y-4">
                {filtered.map((b) => (
                    <div key={b.id} className="bg-white rounded-xl border border-slate-200 p-6 flex flex-col lg:flex-row justify-between gap-6 hover:border-violet-200 transition-all group">
                        <div className="flex-1 space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-violet-50 rounded-xl flex items-center justify-center text-violet-600 group-hover:bg-violet-600 group-hover:text-white transition-all">
                                    <CalendarCheck size={24} />
                                </div>
                                <div>
                                    <h4 className="font-bold text-slate-900">Facility ID: {b.facility_id.slice(0,8)}</h4>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1">
                                        <Clock size={10} /> {new Date(b.created_at).toLocaleString()}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="space-y-0.5">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase">Unit</p>
                                    <p className="text-sm font-semibold text-slate-700">{b.unit_id.slice(0,8)}</p>
                                </div>
                                <div className="space-y-0.5">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase">Date</p>
                                    <p className="text-sm font-semibold text-slate-700">{new Date(b.booking_date).toLocaleDateString()}</p>
                                </div>
                                <div className="space-y-0.5">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase">UTR Ref</p>
                                    <p className="text-sm font-mono font-bold text-emerald-600">{b.payment_ref || 'N/A'}</p>
                                </div>
                                <div className="space-y-0.5">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase">Payment</p>
                                    <StatusBadge status={b.payment_status} className="mt-1" />
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 border-t lg:border-t-0 lg:border-l border-slate-100 pt-4 lg:pt-0 lg:pl-6">
                            {b.payment_screenshot_url && (
                                <a href={b.payment_screenshot_url} target="_blank" rel="noreferrer" className="p-2 bg-slate-50 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-all flex flex-col items-center gap-1">
                                    <ImageIcon size={18} />
                                    <span className="text-[8px] font-bold uppercase">View Proof</span>
                                </a>
                            )}
                            {filter === 'pending' && (
                                <>
                                    <button
                                        onClick={() => handleReconcile(b.id)}
                                        className="bg-emerald-600 text-white px-4 py-2 rounded-lg font-bold text-[10px] uppercase tracking-widest hover:bg-emerald-700 transition-all flex items-center gap-2"
                                    >
                                        <CheckCircle2 size={14} /> Reconcile
                                    </button>
                                    <button
                                        onClick={() => handleReject(b.id)}
                                        className="bg-white text-red-500 border border-red-200 px-4 py-2 rounded-lg font-bold text-[10px] uppercase tracking-widest hover:bg-red-50 transition-all flex items-center gap-2"
                                    >
                                        <XCircle size={14} /> Reject
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ))}

                {filtered.length === 0 && (
                    <div className="py-12 text-center bg-white rounded-xl border border-dashed border-slate-200">
                        <CalendarCheck size={32} className="text-slate-200 mx-auto mb-2" />
                        <p className="text-sm text-slate-400 font-medium">No {filter} bookings found</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BookingApprovals;
